import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Pricing.css';

const Pricing: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const freeFeatures = t('pricing.free.features', { returnObjects: true }) as string[];
  const premiumFeatures = t('pricing.premium.features', { returnObjects: true }) as string[];
  const schoolFeatures = t('pricing.school.features', { returnObjects: true }) as string[];

  return (
    <section className="pricing section" id="pricing">
      <div className="pricing-bg">
        <div className="pricing-pattern"></div>
      </div>
      
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">{t('pricing.title')}</h2>
          <p className="section-subtitle">{t('pricing.subtitle')}</p>
        </motion.div>

        <div className="pricing-grid three-cards">
          {/* Free Plan */}
          <motion.div
            className="pricing-card"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="pricing-header">
              <h3 className="pricing-name">{t('pricing.free.name')}</h3>
              <p className="pricing-description">{t('pricing.free.description')}</p>
            </div>
            <div className="pricing-price">
              <span className="price-amount">{t('pricing.free.price')}</span>
              <span className="price-period">/{t('pricing.free.period')}</span>
            </div>
            <ul className="pricing-features">
              {freeFeatures.map((feature, index) => (
                <li key={index}>
                  <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            <a href="#download" className="btn btn-secondary pricing-cta">
              {t('pricing.free.cta')}
            </a>
          </motion.div>

          {/* Premium Plan */}
          <motion.div
            className="pricing-card premium"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="pricing-header">
              <h3 className="pricing-name">{t('pricing.premium.name')}</h3>
              <p className="pricing-description">{t('pricing.premium.description')}</p>
            </div>
            <div className="pricing-price">
              <span className="price-amount">{t('pricing.premium.price')}</span>
              <span className="price-period">/{t('pricing.premium.period')}</span>
            </div>
            <p className="price-alt">{t('pricing.premium.altPrice')}</p>
            <ul className="pricing-features">
              {premiumFeatures.map((feature, index) => (
                <li key={index}>
                  <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            <a href="#download" className="btn btn-primary pricing-cta">
              {t('pricing.premium.cta')}
            </a>
            <div className="pricing-terms">
              <p>{t('pricing.premium.promoNote')}</p>
            </div>
          </motion.div>

          {/* School Plan */}
          <motion.div
            className="pricing-card school"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="pricing-header">
              <h3 className="pricing-name">{t('pricing.school.name')}</h3>
              <p className="pricing-description">{t('pricing.school.description')}</p>
            </div>
            <div className="pricing-price">
              <span className="price-amount">{t('pricing.school.price')}</span>
              <span className="price-period"> {t('pricing.school.period')}</span>
            </div>
            <ul className="pricing-features">
              {schoolFeatures.map((feature, index) => (
                <li key={index}>
                  <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            <a href="#contact" className="btn btn-outline pricing-cta">
              {t('pricing.school.cta')}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
